import React, { cloneElement, isValidElement, useCallback, useEffect, useRef, useState } from 'react';
import { JSXMap } from 'fennec-ui';
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import MDEditor from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import { Tag } from 'antd';
import { createPortal } from 'react-dom';
import {
    List as VirtualizedList,
    AutoSizer,
    WindowScroller,
} from 'react-virtualized'

var utc = require('dayjs/plugin/utc')
var timezone = require('dayjs/plugin/timezone') // dependent on utc plugin
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('ru');

const { CheckableTag } = Tag;
var _ = require('lodash');

/*----------------------------------------------------------------------------------*/
export function useWindowDimensions() {

    const hasDocument = typeof document?.documentElement !== 'undefined';
    const hasWindow = typeof window !== 'undefined';

    function getWindowDimensions() {
        const clientWidth = hasDocument ? document.documentElement.clientWidth : null;
        const clientHeight = hasDocument ? document.documentElement.clientHeight : null;
        const innerWidth = hasWindow ? window.innerWidth : null;
        const innerHeight = hasWindow ? window.innerHeight : null;
        return {
            clientWidth,
            clientHeight,
            innerWidth,
            innerHeight
        };
    }

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        if (hasWindow) {
            function handleResize() {
                setWindowDimensions(getWindowDimensions());
            }

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [hasWindow]);

    return windowDimensions;
}
/*----------------------------------------------------------------------------------*/

var properties = [
    'direction',  // RTL support
    'boxSizing',
    'width',  // on Chrome and IE, exclude the scrollbar, so the mirror div wraps exactly as the textarea does
    'height',
    'overflowX',
    'overflowY',  // copy the scrollbar for IE

    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderStyle',

    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',

    // https://developer.mozilla.org/en-US/docs/Web/CSS/font
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',

    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',  // might not make a difference, but better be safe

    'letterSpacing',
    'wordSpacing',

    'tabSize',
    'MozTabSize'

];

var isBrowser = (typeof window !== 'undefined');
var isFirefox = (isBrowser && window.mozInnerScreenX != null);

function getCaretCoordinates(element, position, options) {
    if (!isBrowser) {
        throw new Error('textarea-caret-position#getCaretCoordinates should only be called in a browser');
    }

    var debug = options && options.debug || false;
    if (debug) {
        var el = document.querySelector('#input-textarea-caret-position-mirror-div');
        if (el) el.parentNode.removeChild(el);
    }

    // The mirror div will replicate the textarea's style
    var div = document.createElement('div');
    div.id = 'input-textarea-caret-position-mirror-div';
    document.body.appendChild(div);

    var style = div.style;
    var computed = window.getComputedStyle ? window.getComputedStyle(element) : element.currentStyle;  // currentStyle for IE < 9
    var isInput = element.nodeName === 'INPUT';

    // Default textarea styles
    style.whiteSpace = 'pre-wrap';
    if (!isInput)
        style.wordWrap = 'break-word';  // only for textarea-s

    // Position off-screen
    style.position = 'absolute';  // required to return coordinates properly
    if (!debug)
        style.visibility = 'hidden';  // not 'display: none' because we want rendering

    // Transfer the element's properties to the div
    properties.forEach(function (prop) {
        if (isInput && prop === 'lineHeight') {
            // Special case for <input>s because text is rendered centered and line height may be != height
            if (computed.boxSizing === "border-box") {
                var height = parseInt(computed.height);
                var outerHeight =
                    parseInt(computed.paddingTop) +
                    parseInt(computed.paddingBottom) +
                    parseInt(computed.borderTopWidth) +
                    parseInt(computed.borderBottomWidth);
                var targetHeight = outerHeight + parseInt(computed.lineHeight);
                if (height > targetHeight) {
                    style.lineHeight = height - outerHeight + "px";
                } else if (height === targetHeight) {
                    style.lineHeight = computed.lineHeight;
                } else {
                    style.lineHeight = 0;
                }
            } else {
                style.lineHeight = computed.height;
            }
        } else {
            style[prop] = computed[prop];
        }
    });

    if (isFirefox) {
        // Firefox lies about the overflow property for textareas: https://bugzilla.mozilla.org/show_bug.cgi?id=984275
        if (element.scrollHeight > parseInt(computed.height))
            style.overflowY = 'scroll';
    } else {
        style.overflow = 'hidden';  // for Chrome to not render a scrollbar; IE keeps overflowY = 'scroll'
    }

    div.textContent = element.value.substring(0, position);
    // The second special handling for input type="text" vs textarea:
    // spaces need to be replaced with non-breaking spaces - http://stackoverflow.com/a/13402035/1269037
    if (isInput)
        div.textContent = div.textContent.replace(/\s/g, '\u00a0');

    var span = document.createElement('span');
    // Wrapping must be replicated *exactly*, including when a long word gets
    // onto the next line, with whitespace at the end of the line before (#7).
    // The  *only* reliable way to do that is to copy the *entire* rest of the
    // textarea's content into the <span> created at the caret position.
    // For inputs, just '.' would be enough, but no need to bother.
    span.textContent = element.value.substring(position) || '.';  // || because a completely empty faux span doesn't render at all
    div.appendChild(span);

    // let origin = computed['perspectiveOrigin'].split(" ");
    var coordinates = {
        top: span.offsetTop + parseInt(computed['borderTopWidth']),
        left: span.offsetLeft + parseInt(computed['borderLeftWidth']),
        height: parseInt(computed['lineHeight']),
        // originLeft: (origin.length >= 2)?parseInt(origin[0]):0,
        // originTop: (origin.length >= 2)?parseInt(origin[1]):0,
    };

    if (debug) {
        span.style.backgroundColor = '#aaa';
    } else {
        document.body.removeChild(div);
    }

    return coordinates;
}

export const Dropdown = (props, context) => {
    const { open, setOpen, loading, coordinates, items, propertiesForAction, contentStyle, contentClassName, children, virtualized } = props;
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState();
    const elements = useRef({});
    const { clientWidth, clientHeight, innerWidth, innerHeight } = useWindowDimensions();

    const handleAction = useCallback((item) => {
        if (item && item.action) {
            item.action(item, propertiesForAction);
        }
        setOpen(false);
    }, [props]);

    const handleKeyDown = useCallback(
        async (ev) => {
            switch (ev.code) {
                case "ArrowDown":
                    ev.preventDefault();
                    ev.stopPropagation();
                    setSelectedItem(o => {
                        if (!items || items.length <= 0) return;
                        const index = items.findIndex(e => e.key === o.key);
                        if (index >= 0 && index + 1 <= items.length - 1) {
                            elements.current[index + 1]?.scrollIntoView({
                                block: "nearest"
                            });
                            return items[index + 1]
                        }
                        return o;
                    })
                    break;
                case "ArrowUp": {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setSelectedItem(o => {
                        if (!items || items.length <= 0) return;
                        const index = items.findIndex(e => e.key === o.key);
                        if (index >= 0 && index - 1 >= 0) {
                            elements.current[index - 1]?.scrollIntoView({
                                block: "nearest"
                            });
                            return items[index - 1]
                        }
                        return o;
                    })
                    break;
                }
                case "NumpadEnter":
                case "Enter": {
                    ev.preventDefault();
                    ev.stopPropagation();
                    const item = items.find(e => e.key === selectedItem.key);
                    if (item) {
                        handleAction(item);
                    }
                    break;
                }
                case "Escape": {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setOpen(false);
                    break;
                }
            }
        },
        [items, selectedItem]
    );

    useEffect(() => {
        if (!open) return;

        setDetailOpen(true);
    }, [open]);

    useEffect(() => {
        if ((!items || items.length <= 0) && !selectedItem) return;
        setSelectedItem(items[0])
    }, [items]);

    useEffect(() => {
        if (open) {
            document.addEventListener("keydown", handleKeyDown, true);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
        };
    }, [open, handleKeyDown]);

    const dropdownRef = useRef(null);
    const detailRef = useRef(null);

    const showDetail = detailOpen && selectedItem && selectedItem.detail;
    const dropdownWidth = 300;
    const dropdownMaxHeight = 200;
    const detailMaxWidth = 350;
    const detailMaxHeight = detailMaxWidth;

    const dropdown_coordinates = (dropdownRef.current) ? dropdownRef.current.getBoundingClientRect() : undefined;
    const detail_coordinates = (detailRef.current) ? detailRef.current.getBoundingClientRect() : undefined;
    const dropdown_coordinates_height = (dropdown_coordinates?.height) ? dropdown_coordinates?.height : 0;
    const dropdown_coordinates_width = (dropdown_coordinates?.width) ? dropdown_coordinates?.width : 0;
    const detail_coordinates_height = (detail_coordinates?.height) ? detail_coordinates?.height : 0;
    const detail_coordinates_width = (detail_coordinates?.width) ? detail_coordinates?.width : 0;
    const coordinates_left = (coordinates?.left) ? parseInt(coordinates?.left) : 0;
    const coordinates_top = (coordinates?.top) ? parseInt(coordinates?.top) : 0;
    const coordinates_height = (coordinates?.height) ? parseInt(coordinates?.height) : 0;
    const coordinates_textareascroll_left = (coordinates?.textareaScrollLeft) ? parseInt(coordinates?.textareaScrollLeft) : 0;
    const coordinates_textareascroll_top = (coordinates?.textareaScrollTop) ? parseInt(coordinates?.textareaScrollTop) : 0;
    /*--- Размещение по умолчанию [курсор - dropdown -> detail] ---*/
    var dropdown_coordinates_left = coordinates_left - coordinates_textareascroll_left;
    var dropdown_coordinates_top = coordinates_top + coordinates_height - coordinates_textareascroll_top;
    var detail_coordinates_left = dropdown_coordinates_left + dropdownWidth + 2;
    var detail_coordinates_top = dropdown_coordinates_top;

    // console.log(dropdown_coordinates_left, dropdown_coordinates_top, coordinates);

    /*--- dropdown не помещается справа от курсора ---*/
    if (dropdown_coordinates_left + ((showDetail) ? dropdownWidth + detailMaxWidth + 2 : dropdownWidth) >= clientWidth) {
        dropdown_coordinates_left = clientWidth - ((showDetail) ? dropdownWidth + detailMaxWidth + 2 : dropdownWidth) - 2;
        detail_coordinates_left = dropdown_coordinates_left + dropdownWidth + 2;
    }
    /*--- dropdown не помещается снизу от курсора ---*/
    if (dropdown_coordinates_top + dropdown_coordinates_height >= clientHeight) {
        dropdown_coordinates_top = coordinates_top - dropdown_coordinates_height;
        detail_coordinates_top = dropdown_coordinates_top;
    }
    const renderItem = useCallback((item, index, key, style) => {
        if (item?.render) {
            let child = item.render(item, index);
            if (isValidElement(child)) {
                return <div key={key} style={{ ...style }}>
                    {cloneElement(child, {
                        selected: item.key === selectedItem?.key,
                        onMouseEnter: () => {
                            setSelectedItem(item)
                        },
                        onClick: (ev) => {
                            ev.stopPropagation();
                            handleAction(item);
                        },
                        ref: (node) => {
                            return (elements.current[index] = node)
                        }
                    })}
                </div>
            }
        }
    }, [selectedItem, elements])

    function rowRenderer({
        index,
        key,
        style,
    }) {
        const item = items[index]
        if (!item) return
        return renderItem(item, index, key, style)
    }

    return (
        <div>
            {children}
            {open &&
                coordinates &&
                createPortal(
                    <>
                        <div style={{
                            position: "fixed",
                            inset: "0"
                        }} onClick={() => setOpen(false)} />
                        <div ref={dropdownRef} className={contentClassName}
                            style={{
                                zIndex: "1000",

                                position: "absolute",

                                left: `${dropdown_coordinates_left}px`,
                                top: `${dropdown_coordinates_top}px`,

                                width: `${dropdownWidth}px`,
                                maxHeight: `${dropdownMaxHeight}px`,
                                overflowY: "auto",
                                overflowX: "hidden",
                                ...contentStyle
                            }}>
                            {(loading) && <div style={{
                                textAlign: "center",
                                fontSize: "13px"
                            }}>
                                Загрузка...
                            </div>}
                            {((!items || items.length <= 0) && !loading) && <div style={{
                                textAlign: "center",
                                fontSize: "13px"
                            }}>
                                Нет предположений
                            </div>}
                            {(items && items.length > 0 && virtualized === true && !loading) && <WindowScroller onScroll={({ scrollTop }) => {
                                // console.log('scrollTop', scrollTop)
                            }}>
                                {({ height, scrollTop, isScrolling }) => (
                                    <div>
                                        <AutoSizer disableHeight>
                                            {({ width }) => (
                                                <VirtualizedList
                                                    autoHeight
                                                    rowCount={items.length}
                                                    rowRenderer={rowRenderer}
                                                    width={width}
                                                    height={height}
                                                    // rowHeight={36}
                                                    rowHeight={({ index }) => {
                                                        const item = items[index]
                                                        if (!item?.rowHeight) return 18
                                                        return item?.rowHeight
                                                    }}
                                                    overscanRowCount={10}
                                                    isScrolling={isScrolling}
                                                    scrollTop={scrollTop}
                                                />
                                            )}
                                        </AutoSizer>
                                    </div>
                                )}
                            </WindowScroller>}
                            {(items && items.length > 0 && virtualized !== true && !loading) && JSXMap(items, (item, idx) => renderItem(item, idx))}
                        </div>
                        {showDetail &&
                            <div ref={detailRef} className={contentClassName}
                                style={{
                                    zIndex: "1001",
                                    position: "absolute",

                                    left: `${detail_coordinates_left}px`,
                                    top: `${detail_coordinates_top}px`,

                                    maxWidth: `${detailMaxWidth}px`,
                                    maxHeight: `${detailMaxHeight}px`,
                                    overflow: "auto",
                                    ...contentStyle
                                }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <div style={{ flex: "1 1 auto", textAlign: "left", fontSize: "12px", fontWeight: "600", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "276px" }}>
                                        {selectedItem?.name}
                                    </div>
                                    <CheckableTag
                                        style={{ flex: "0 0", marginRight: "0px" }}
                                        key={"close"}
                                        onClick={() => setDetailOpen(false)}
                                    >
                                        <i className="fa fa-times" style={{ fontSize: "10px", color: "#5e5e5e" }}></i>
                                    </CheckableTag>
                                </div>
                                <div style={{ fontSize: "13px" }}>
                                    {_.isFunction(selectedItem?.detail) && selectedItem?.detail()}
                                    {_.isArray(selectedItem?.detail) && selectedItem?.detail}
                                    {_.isString(selectedItem?.detail) && <MDEditor.Markdown rehypePlugins={[[rehypeSanitize]]} source={selectedItem?.detail} />}
                                </div>
                            </div>
                        }
                    </>,
                    document.body
                )
            }
        </div >
    );
};

export default function Autocomplete(props) {
    const { auth, item, value, onChange, onAfterChange, triggers, virtualized } = props;
    const textareaRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState();
    const [filter, setFilter] = useState({ start: -1, end: -1, trigger: "" });

    const contentStyle = {
        backgroundColor: "white", //token.colorBgElevated,
        borderRadius: "skyblue",//token.borderRadiusLG,
        boxShadow: "0 6px 16px 0 rgb(0 0 0 / 8%), 0 3px 6px -4px rgb(0 0 0 / 12%), 0 9px 28px 8px rgb(0 0 0 / 5%)", //token.boxShadowSecondary,
        borderRadius: "6px",
        padding: "4px",
        listStyleType: "none",
        backgroundClip: "padding-box",
        outline: "none"
    };

    const getPromiseFromProvider = React.useCallback((triggerSettings, filter) => {
        const { dataProvider } = triggerSettings;

        if (typeof dataProvider !== "function") {
            throw new Error("Trigger provider has to be a function!");
        }

        let providedData = dataProvider(filter);

        if (!(providedData instanceof Promise)) {
            providedData = Promise.resolve(providedData);
        }
        return providedData;
    }, [filter]);

    const triggersMatch = useCallback((trigger) => {
        let m = [];
        for (const key in triggers) {
            if (Object.hasOwnProperty.call(triggers, key)) {
                const element = triggers[key];
                var match = trigger?.match(new RegExp(key, "gi"));
                if (match) {
                    m.push(element)
                }
            }
        }
        return m;
    }, [triggers])

    const refreshDataProviders = useCallback((tr) => {
        let trgs = tr || triggersMatch(filter?.trigger)
        setItems([]);
        setLoading(true);
        Promise.all(trgs.map(element => getPromiseFromProvider(element, filter?.trigger))).then((values) => {
            setLoading(false);
            setItems(_.flatten(values));
        });

    }, [triggers, filter, setFilter, items, setItems]);

    useEffect(() => {
        if (open === true) {
            refreshDataProviders()
            // } else {
            //     let t = triggersMatch(filter?.trigger)?.filter(e=>e.onCaretChange);
            //     console.log(filter?.trigger, t);
            //     if(t && t.length > 0){
            //         setOpen(true);
            //     }
        }
    }, [open, filter])

    const openHandler = React.useCallback(() => {
        if (!open === true) {
            refreshDataProviders();
        } else {
            setFilter({ start: -1, end: -1, trigger: "" });
        }

        setOpen(o => {
            return !o
        })
    }, [open, triggers, filter, setFilter, setItems])


    const getSelectedText = () => {
        if (!textareaRef.current) return null;
        const { selectionStart, selectionEnd } = textareaRef.current;

        if (selectionStart === selectionEnd) return null;

        return value.substr(
            selectionStart,
            selectionEnd - selectionStart
        );
    };

    const setCaretPosition = (position = 0) => {
        if (!textareaRef.current) return;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(position, position);
    };

    const getCaretPosition = () => {
        if (!textareaRef.current) {
            return 0;
        }
        const position = textareaRef.current.selectionEnd;
        return position;
    };

    const lastIndexOfRegex = (text, regex, fromIndex) => {
        var str = fromIndex ? text?.substring(0, fromIndex) : text;
        var match = str?.match(regex);
        return match ? str?.lastIndexOf(match[match.length - 1]) : -1;
    }

    const _handleCaretChange = useCallback((e) => {
        if (e.type !== "keydown" && e.code !== "ArrowUp" && e.code !== "ArrowDown") {
            let pos = getCaretPosition();
            let idx = lastIndexOfRegex(value, /\s/gi, pos) + 1;
            let trigger = value?.slice(idx, pos);

            setFilter({
                start: idx,
                end: pos,
                trigger
            })
        }
        if (e.type === "keydown") {
            const code = e.keyCode || e.which;
            if (e.code === "Space" && e.ctrlKey === true) {
                openHandler();
                e.preventDefault();
            }
        }
    }, [value, filter, setFilter]);
    const textarea_coordinates = (textareaRef.current) ? textareaRef.current.getBoundingClientRect() : undefined;

    const c = (textareaRef.current) ? getCaretCoordinates(
        textareaRef.current,
        textareaRef.current?.selectionEnd
    ) : undefined;
    const scroll_left = (window.pageXOffset || document.documentElement.scrollLeft) - (document.documentElement.clientLeft || 0);
    const scroll_top = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0);
    const coordinates = (textareaRef.current) ? {
        ...textarea_coordinates,
        height: /*textarea_coordinates?.height +*/ c?.height,
        left: textarea_coordinates?.left + c?.left + 3 + scroll_left,
        top: textarea_coordinates?.top + c?.top + scroll_top,
        textareaScrollTop: textareaRef.current.scrollTop,
        textareaScrollLeft: textareaRef.current.scrollLeft,
    } : undefined;

    const insertAtCursor = useCallback((text, start, end) => {
        // const beforeHandle = getCaretPosition() - 1;

        //IE support
        if (document.selection) {
            textareaRef.current.focus();
            var sel = document.selection.createRange();
            sel.text = text;
            // onChange(text);
        }
        //MOZILLA and others
        else if (textareaRef.current.selectionStart || textareaRef.current.selectionStart == '0') {
            var startPos = start || textareaRef.current.selectionStart;
            var endPos = end || textareaRef.current.selectionEnd;
            var scrollTop = textareaRef.current.scrollTop;
            textareaRef.current.value = textareaRef.current.value.substring(0, startPos)
                + text
                + textareaRef.current.value.substring(endPos, textareaRef.current.value.length);
            onChange(textareaRef.current.value);

            textareaRef.current.focus();
            textareaRef.current.selectionStart = startPos + text.length;
            textareaRef.current.selectionEnd = startPos + text.length;
            textareaRef.current.scrollTop = scrollTop;

            // var textEvent = document.createEvent('TextEvent');
            // textEvent.initTextEvent('textInput', true, true, null, component.value, 2);
            // component.focus();
            // component.dispatchEvent(textEvent);
        } else {
            textareaRef.current.value += text;
            onChange(textareaRef.current.value);
            // var textEvent = document.createEvent('TextEvent');
            // textEvent.initTextEvent('textInput', true, true, null, component.value, 2);
            // component.focus();
            // component.dispatchEvent(textEvent);
        }
    }, [textareaRef.current])

    return (
        <Dropdown
            open={open}
            loading={loading}
            setOpen={setOpen}
            coordinates={coordinates}
            virtualized={item?.virtualized || virtualized}
            propertiesForAction={{
                insertAtCursor,
                ...filter
            }}

            items={items}

            contentStyle={contentStyle}
            contentClassName={'small-scroll'}
        >
            <textarea
                ref={textareaRef}
                style={{ width: "100%", minHeight: "150px", fontSize: "13px" }}
                value={value}
                onChange={onChange}
                onFocus={_handleCaretChange}
                onClick={_handleCaretChange}
                onKeyUp={_handleCaretChange}
                onKeyDown={_handleCaretChange}>
            </textarea>
        </Dropdown >
    )
}